// MARK://dhk8/pos/lib/clients/modifiers
// Typed client for dhk8-catalog-modifiers — reconciled against the published contract
// at services/dhk8-catalog-modifiers/src/contract.ts (commit 7349ba2).
//
// Routes are still /api/modifier-groups... (deployed state); the contract carries a
// TODO(reconcile) to migrate the 3 catalog services to /v1/ with the ingress — when
// that happens, switch BASE here. Envelope: { ok, data } | bare T.
//
// NOTE: GET /api/modifier-groups filters by tenantId only — there is no documented
// per-product read endpoint. We pass `productId` as an extra query param hopefully
// honored by the backend (a product↔group link exists via POST .../products/:id/link);
// if it's ignored, the loader gets all the tenant's groups. TODO(backend): add a
// `?productId=` filter or GET /api/products/:id/modifier-groups.

import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { gqlClient } from '../gql.js'
import { PosModifierGroupsForProductQuery, PosModifierGroupsQuery } from '../queries/modifiers.queries.js'

// dhk8-catalog-modifiers expone /v1/modifier-groups. Ingress route es
// /v1/dhk8-catalog-modifiers/v1/modifier-groups → el servicio ve /v1/modifier-groups.
const BASE = svcUrl('dhk8-catalog-modifiers', '/v1/modifier-groups')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── DTOs (mirror of services/dhk8-catalog-modifiers/src/contract.ts) ────────────

export interface ModifierOptionDto {
  id: string
  groupId: string
  label: string
  priceDeltaCents: number
  nestedGroupId: string | null
  active: boolean
  sortOrder: number
}

export interface ModifierGroupDto {
  id: string
  tenantId: string
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
  active: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  options: ModifierOptionDto[]
}

export interface ModifierGroupListResponse {
  items: ModifierGroupDto[]
  total: number
}

export interface ListModifierGroupsParams {
  tenantId?: string
  productId?: string
  page?: number
  limit?: number
}

export async function listModifierGroups(params: ListModifierGroupsParams = {}, signal?: AbortSignal): Promise<ModifierGroupListResponse> {
  // F4.4 / ADR-0009: lectura por el grafo (catalog-modifiers, tenant del JWT) — mismas
  // queries que Admin. Con productId usa modifierGroupsForProduct; si no, modifierGroups.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  if (params.productId) {
    const res = await gqlClient.query(PosModifierGroupsForProductQuery, { productId: params.productId }, ctx).toPromise()
    if (res.error) throw res.error
    const items = (res.data?.modifierGroupsForProduct ?? []) as unknown as ModifierGroupDto[]
    return { items, total: items.length }
  }
  const vars: { q?: string; page?: number; limit?: number } = {}
  if (params.page) vars.page = params.page
  if (params.limit) vars.limit = params.limit
  const res = await gqlClient.query(PosModifierGroupsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pg = res.data?.modifierGroups
  return { items: (pg?.items ?? []) as unknown as ModifierGroupDto[], total: pg?.total ?? 0 }
}
